import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import PropTypes from 'prop-types';
import RepositoryTree from './RepositoryTree';
import MonacoFileEditor from './MonacoFileEditor';
import Icon from './icons/Icon';
import indexedDbFileStorage from '../utils/indexedDbFileStorage';
import * as vfsClient from '../utils/vfsClient';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import HexagonalLoader from './HexagonalLoader';
import CodeTerminalDock from './terminal/CodeTerminalDock';
import './CodeViewWithLogger.css';
import './repoFlags/RepoFlags.css';
import { devLog } from '../utils/devLog';
import { serviceLabel } from '../utils/codeRoute';
import { useCompact, useNarrowElement } from '../hooks/useMediaQuery';
import useRepoFlags from '../hooks/useRepoFlags';
import { projectPathOf } from '../utils/repoFlags';

// Blob URL of the PDF currently shown; revoked when another one replaces it so the bytes can be freed.
let currentBinaryUrl = null;

const IMAGE_MIME_BY_EXTENSION = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  webp: 'image/webp',
  tiff: 'image/tiff',
  tif: 'image/tiff'
};

/**
 * The bytes of a project file (a PDF, an image) as a blob URL, fetched straight from vfs-service: the IndexedDB copy of
 * a vfs file is text, which corrupts binary content. The editor's (serviceName, filePath) pair maps to the vfs path
 * `serviceName/filePath`, except for the project root ("Home"), where the path is already root-relative.
 */
async function loadBinaryBlobUrl(projectId, serviceName, filePath, mimeType) {
  const atRoot = !serviceName || serviceName === '.' || serviceName === 'Home';
  const candidates = atRoot ? [filePath] : [`${serviceName}/${filePath}`, filePath];
  for (const candidate of candidates) {
    try {
      const blob = await vfsClient.readBinaryFile(projectId, candidate, mimeType);
      if (blob) {
        if (currentBinaryUrl) { URL.revokeObjectURL(currentBinaryUrl); }
        currentBinaryUrl = URL.createObjectURL(blob);
        return currentBinaryUrl;
      }
    } catch (error) {
      devLog('Binary read failed for', candidate, error);
    }
  }
  return null;
}

const SIDEBAR_WIDTH_KEY = 'code-view-sidebar-width';
const SIDEBAR_MIN = 180;
const SIDEBAR_MAX = 720;
const SIDEBAR_DEFAULT = 300;
// How long the editor waits for a linked file (see `settledAutoOpen`) before showing "Select a file to edit".
const AUTO_OPEN_WAIT_MS = 15000;

/** The editor's loading state: the same one a file shows while its content loads. */
const LoadingFile = () => (
  <div className="loading-state" data-testid="code-view-loading-file">
    <Icon name="loading" size={24} />
    <p>Loading file...</p>
  </div>
);

/**
 * CodeViewWithLogger Component
 * 
 * Provides a comprehensive code editing experience with:
 * - File tree view (RepositoryTree)
 * - Monaco editor for editing
 * - IndexedDB-first approach for file storage
 * - Git pull/push integration for version control
 * - Project context management after git operations
 * - Deployment pipeline management
 * - Operation logging to show user what's happening
 * 
 * Workflow:
 * 1. On mount, try to load files from IndexedDB
 * 2. If no files in IndexedDB, pull from git and save to IndexedDB
 * 3. Show tree view and Monaco editor
 * 4. Listen for git pull/push events and update project context
 * 5. Log all operations to user
 * 
 * Exposed methods via ref:
 * - pullFromGit(): Pull latest changes from remote repository
 * - pushToGit(): Push local changes to remote repository
 * - triggerDeployment(): Trigger deployment pipeline
 */
const CodeViewWithLogger = forwardRef(({
  autoOpenPath = null,
  autoOpenLine = null,
  // A Code-tab link names a file that must be there: when the tree does not list it, the editor says so.
  autoOpenStrict = false,
  projectId,
  serviceName,
  repositoryUrl,
  branch = 'main',
  onFileSelect,
  onFileChange,
  onSave,
  initialFilePath = null,
  readOnly = false,
  isPreviewMode = false,
  // Project context for git operations
  selectedProject = null,
  selectedRevision = null,
  selectedTenant = null,
  selectedResource = null,
  configuration = null,
  collections = null,
  onProjectContextChange = null,
  // Preview refresh
  previewUrl = null,
  setPreviewUrl = null,
  setPreviewError = null,
  setForceGitFetch = null,
  autoTriggeredPullRef = null,
  onSwitchToChat = null,
  // See RepositoryTree.jsx's own updateUrlWithFile comment: default true
  // preserves existing Code-mode behavior; the Dashboard/Files, embedded
  // Chat Files tab, and /brain adapters pass false since they browse a
  // synthetic project-root/brain "service" that must never overwrite the
  // page's real route identity.
  syncUrlOnFileSelect = true,
  // See RepositoryTree.jsx's own forceFreshTree comment: default false
  // preserves existing behavior everywhere else; only the global Brain page
  // passes true.
  forceFreshTree = false,
  // The bottom panel's Terminal tab (the Code tab only): asked to vfs-service, never in a read-only view.
  terminal = false
}, ref) => {
  // The in-app Operation Log panel was removed — real operational feedback
  // now lives only in the browser devtools console (plain console.* calls
  // elsewhere in this file). addLog is kept as a no-op so the many existing
  // call sites below don't need individual removal.
  const addLog = useCallback(() => {}, []);

  // Resizable file-tree sidebar (persisted per browser).
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY), 10);
      if (Number.isFinite(saved)) return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, saved));
    } catch { /* storage unavailable */ }
    return SIDEBAR_DEFAULT;
  });
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const sidebarRef = useRef(null);
  // Phones and tablets in portrait: the file tree is a drawer over a full-width editor; picking a file closes it.
  const mainRef = useRef(null);
  const narrowMain = useNarrowElement(mainRef, 640);
  const compactWindow = useCompact();
  const compact = compactWindow || narrowMain;
  const compactRef = useRef(compact);
  compactRef.current = compact;
  const [treeOpen, setTreeOpen] = useState(true);
  useEffect(() => {
    if (!compact || !treeOpen) { return undefined; }
    const onKey = (event) => { if (event.key === 'Escape') { setTreeOpen(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [compact, treeOpen]);

  const persistSidebarWidth = useCallback((width) => {
    try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(Math.round(width))); } catch { /* ignore */ }
  }, []);

  const handleSidebarResizeStart = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setIsResizingSidebar(true);
  }, []);

  const handleSidebarResizeMove = useCallback((e) => {
    if (!isResizingSidebar || !sidebarRef.current) return;
    const left = sidebarRef.current.getBoundingClientRect().left;
    setSidebarWidth(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, e.clientX - left)));
  }, [isResizingSidebar]);

  const handleSidebarResizeEnd = useCallback((e) => {
    if (!isResizingSidebar) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setIsResizingSidebar(false);
    persistSidebarWidth(sidebarRef.current?.getBoundingClientRect().width ?? sidebarWidth);
  }, [isResizingSidebar, persistSidebarWidth, sidebarWidth]);

  const handleSidebarResizeKey = useCallback((e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const step = e.shiftKey ? 48 : 16;
    const next = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, sidebarWidth + (e.key === 'ArrowRight' ? step : -step)));
    setSidebarWidth(next);
    persistSidebarWidth(next);
  }, [sidebarWidth, persistSidebarWidth]);
  const [selectedFile, setSelectedFile] = useState(null);
  // A file in a repo flagged Read-only (utils/repoFlags.js): the editor does not take edits, and says why.
  const repoFlags = useRepoFlags(projectId);
  const lockedRepo = selectedFile ? repoFlags.flagsOf(projectPathOf(serviceName, selectedFile)) : null;
  const repoLocked = Boolean(lockedRepo?.readOnly);
  const [selectedFileObject, setSelectedFileObject] = useState(null); // Store full file object with metadata
  // The file a link asked for that is not in the tree (`autoOpenStrict`), with the closest files; and one of those
  // closest files, picked: opened in the tree the same way as a link. The message only stands while the link still
  // asks for that file and no file has opened since; a new link drops the pick.
  const [missingFileState, setMissingFile] = useState(null);
  const [pickedPath, setPickedPath] = useState(null);
  useEffect(() => { setPickedPath(null); }, [autoOpenPath, serviceName]);
  const missingFile = missingFileState && missingFileState.service === serviceName && missingFileState.path === (pickedPath || autoOpenPath) ? missingFileState : null;
  const handleAutoOpenMissing = useCallback((info) => setMissingFile({ ...info, service: serviceName }), [serviceName]);
  // The file a link (or a reload) names, until it opens or is known to be missing: the editor shows the loading state,
  // not "Select a file to edit" for the second the tree takes to load. Settled once per link; after
  // AUTO_OPEN_WAIT_MS the placeholder comes back anyway (a tree that never answers).
  const [settledAutoOpen, setSettledAutoOpen] = useState(null);
  const autoOpenKey = autoOpenPath ? `${serviceName}|${autoOpenPath}` : null;
  useEffect(() => {
    if (!autoOpenKey) { return undefined; }
    const timer = setTimeout(() => setSettledAutoOpen(autoOpenKey), AUTO_OPEN_WAIT_MS);
    return () => clearTimeout(timer);
  }, [autoOpenKey]);
  useEffect(() => {
    if (autoOpenKey && (selectedFile || missingFile)) { setSettledAutoOpen(autoOpenKey); }
  }, [autoOpenKey, selectedFile, missingFile]);
  const waitingForLinkedFile = Boolean(autoOpenKey) && settledAutoOpen !== autoOpenKey && !selectedFile && !missingFile;
  const [selectedLineNumber, setSelectedLineNumber] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [hasLoadedFromIndexedDB, setHasLoadedFromIndexedDB] = useState(false);
  const [fileTree, setFileTree] = useState([]);
  const [error, setError] = useState(null);
  const [fileErrors, setFileErrors] = useState({}); // Track file errors for tree view
  const [isPullingFromGit, setIsPullingFromGit] = useState(false); // Track git pull operation
  // Independent of RepositoryTree's own internal tree state (not lifted up
  // to this component) — a small direct check via the same
  // vfsClient.isServiceEmpty Canvas.jsx's preview tab already uses, so the
  // Code tab can show the same "empty project" message instead of the bare
  // "Select a file to edit" placeholder for a project with no files yet.
  // `null` = not checked yet (avoids flashing the empty state during the
  // brief window before the check resolves).
  const [isEmptyProject, setIsEmptyProject] = useState(null);
  const initializingRef = useRef(false); // Prevent concurrent initializations
  const lastInitializedRef = useRef(null); // Track last initialized project+service
  const currentOpenFileRef = useRef(null);

  // Track which file is currently open
  useEffect(() => {
    currentOpenFileRef.current = selectedFile || null;
  }, [selectedFile]);

  // See isEmptyProject declaration above.
  //
  // Bug (reported live): opening Code while a chat is still mid-creation
  // for this exact project (Home's embedded chat, or any freshly-created
  // project someone jumps to Code on early) landed here before the agent's
  // first vfs_write_file/vfs_patch had actually completed server-side —
  // this one-shot check correctly saw zero files at that instant, and
  // since nothing ever re-checked, the "Empty project" placeholder stuck
  // around forever even after the chat went on to write real files (no
  // remount happens; the URL's project id was already correct the whole
  // time — this was never actually a second/duplicate project, just a
  // stale one-shot read of a project that hadn't been written to yet).
  //
  // RepositoryTree.jsx already solves this exact problem for the file
  // TREE via `previewFilesUpdated` (VfsChatPanel dispatches it, debounced,
  // after every successful vfs_write_file/vfs_patch during a live agent
  // turn) — this state just never listened for it. Re-check on that same
  // event now, so both flip together. A short bounded poll stays as a
  // fallback for the (unlikely) case the event is missed, so a project
  // that's genuinely empty still settles into that state quickly.
  useEffect(() => {
    const vfsBacked = Boolean(selectedResource?.data?.vfsBacked);
    if (!projectId || !serviceName || !vfsBacked) {
      setIsEmptyProject(null);
      return undefined;
    }
    let isCancelled = false;
    let attempt = 0;
    const MAX_ATTEMPTS = 6;
    const RETRY_MS = 3000;
    let timeoutId = null;
    setIsEmptyProject(null);
    const check = () => {
      vfsClient.isServiceEmpty(projectId, serviceName).then((empty) => {
        if (isCancelled) return;
        setIsEmptyProject(empty);
        attempt += 1;
        if (empty && attempt < MAX_ATTEMPTS) {
          timeoutId = setTimeout(check, RETRY_MS);
        }
      });
    };
    check();
    const handlePreviewFilesUpdated = (event) => {
      const {projectId: eventProjectId, serviceName: eventServiceName} = event?.detail || {};
      if (eventProjectId === projectId && eventServiceName === serviceName) {
        if (timeoutId) clearTimeout(timeoutId);
        attempt = 0;
        check();
      }
    };
    window.addEventListener('previewFilesUpdated', handlePreviewFilesUpdated);
    return () => {
      isCancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('previewFilesUpdated', handlePreviewFilesUpdated);
    };
  }, [projectId, serviceName, selectedResource?.data?.vfsBacked]);
  
  // Memoize the file object to prevent re-creation and loops
  const memoizedFileObject = useMemo(() => {
    if (selectedFileObject) {
      return selectedFileObject;
    }
    if (selectedFile) {
      return {
        path: selectedFile,
        name: selectedFile.split('/').pop(),
        content: fileContent,
        language: selectedFile.split('.').pop(),
        lineNumber: selectedLineNumber
      };
    }
    return null;
  }, [selectedFileObject, selectedFile, selectedLineNumber]); // Removed fileContent to prevent loop

  /**
   * Load files from IndexedDB
   * @returns {Promise<Array>} Array of files from IndexedDB
   */
  const loadFromIndexedDB = useCallback(async () => {
    if (!projectId || !serviceName) {
      addLog('⚠️ Missing project or service information', 'warning');
      return [];
    }

    try {
      addLog('🔍 Checking IndexedDB for cached files...', 'info');
      const files = await indexedDbFileStorage.getAllFiles(projectId, serviceName);
      
      if (files && files.length > 0) {
        addLog(`✅ Found ${files.length} files in IndexedDB cache`, 'success');
        setHasLoadedFromIndexedDB(true);
        
        // Convert to tree format
        const treeData = files.map(file => ({
          path: file.filePath,
          type: 'blob',
          name: file.fileName || file.filePath.split('/').pop(),
          id: file.id,
          state: file.state,
          lastModified: file.lastModified
        }));
        
        setFileTree(treeData);
        return files;
      } else {
        addLog('📭 No files found in IndexedDB', 'info');
        return [];
      }
    } catch (error) {
      addLog(`❌ Error loading from IndexedDB: ${error.message}`, 'error');
      console.error('Error loading from IndexedDB:', error);
      return [];
    }
  }, [projectId, serviceName, addLog]);

  // Listen for Git operation events
  useEffect(() => {
    const handleGitPushRequested = () => {
      // Detect provider from service data
      const service = JSON.parse(localStorage.getItem(`service_${projectId}_${serviceName}`) || '{}');
      const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
      const provider = repoUrl?.includes('github.com') ? 'GitHub' : 'GitLab';
      addLog(`🚀 Push requested - sending changes to ${provider}...`, 'info', 'git');
    };

    const handleGitPushCompleted = (event) => {
      const {success, filesCount, error} = event.detail || {};
      if (success === false) {
        addLog(`❌ Push failed: ${error || 'Unknown error'}`, 'error', 'git');
      } else {
        addLog(`✅ Push completed successfully (${filesCount || 0} file(s))`, 'success', 'git');
      }
    };

    const handleGitPullRequested = () => {
      // Detect provider from service data
      const service = JSON.parse(localStorage.getItem(`service_${projectId}_${serviceName}`) || '{}');
      const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
      const provider = repoUrl?.includes('github.com') ? 'GitHub' : 'GitLab';
      addLog(`⬇️ Pull requested - fetching latest from ${provider}...`, 'info', 'git');
      setIsPullingFromGit(true);
    };

    const handleGitPullCompleted = async (event) => {
      const {success, error} = event.detail || {};
      if (success === false) {
        addLog(`❌ Pull failed: ${error || 'Unknown error'}`, 'error', 'git');
        setIsPullingFromGit(false);
      } else {
        // Detect provider from service data
        const service = JSON.parse(localStorage.getItem(`service_${projectId}_${serviceName}`) || '{}');
        const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
        const provider = repoUrl?.includes('github.com') ? 'GitHub' : 'GitLab';
        addLog(`✅ Pull completed - files updated from ${provider}`, 'success', 'git');
        // Reload file tree from IndexedDB after pull completes
        addLog('🔄 Refreshing file tree...', 'info');
        const files = await loadFromIndexedDB();
        setIsPullingFromGit(false);
        
        // Log success with file count
        if (files && files.length > 0) {
          addLog(`✅ ${files.length} file(s) loaded successfully`, 'success');
        }
      }
    };

    window.addEventListener('gitPushRequested', handleGitPushRequested);
    window.addEventListener('gitPushCompleted', handleGitPushCompleted);
    window.addEventListener('gitPullRequested', handleGitPullRequested);
    window.addEventListener('gitPullCompleted', handleGitPullCompleted);

    return () => {
      window.removeEventListener('gitPushRequested', handleGitPushRequested);
      window.removeEventListener('gitPushCompleted', handleGitPushCompleted);
      window.removeEventListener('gitPullRequested', handleGitPullRequested);
      window.removeEventListener('gitPullCompleted', handleGitPullCompleted);
    };
  }, [addLog, loadFromIndexedDB]);

  /**
   * Pull files from git repository and save to IndexedDB
   */
  const pullFromGit = useCallback(async () => {
    if (!repositoryUrl) {
      addLog('⚠️ No repository URL provided', 'warning');
      setError('No repository URL configured for this service');
      return;
    }

    if (!projectId || !serviceName) {
      addLog('⚠️ Missing project ID or service name', 'warning');
      return;
    }

    addLog('🔄 Dispatching pull request...', 'info');

    try {
      // Dispatch event - RepositoryTree will handle the actual pull
      window.dispatchEvent(new CustomEvent('gitPullRequested', {
        detail: {
          projectId,
          serviceName,
          branch
        }
      }));

      addLog('✅ Pull request dispatched', 'info');

    } catch (error) {
      addLog(`❌ Error pulling from git: ${error.message}`, 'error');
      // eslint-disable-next-line no-console
      console.error('Error pulling from git:', error);
      setError(error.message);
    }
  }, [repositoryUrl, projectId, serviceName, branch, addLog]);
  useEffect(() => {
    const initialize = async () => {
      // Prevent concurrent initializations
      if (initializingRef.current) {
        addLog('⏳ Already initializing, skipping...', 'info');
        return;
      }
      
      // Check if already initialized for this project+service
      const currentKey = `${projectId}:${serviceName}`;
      const isAlreadyInitialized = lastInitializedRef.current === currentKey;
      
      if (isAlreadyInitialized) {
        // Already initialized but refresh the file tree display in case files changed
        addLog('🔄 Refreshing file tree from IndexedDB...', 'info');
        await loadFromIndexedDB();
        return;
      }
      
      initializingRef.current = true;
      addLog('🚀 Initializing Code View...', 'info');
      
      try {
        // Try loading from IndexedDB first
        const cachedFiles = await loadFromIndexedDB();
        
        // If no cached files, pull from git
        if (cachedFiles.length === 0) {
          addLog('📥 No cached files found, pulling from Git...', 'info');
          await pullFromGit();
        } else {
          addLog('✨ Ready to edit!', 'success');
        }
        
        // Mark as successfully initialized
        lastInitializedRef.current = currentKey;
      } catch (error) {
        addLog(`❌ Initialization failed: ${error.message}`, 'error');
        console.error('Initialization error:', error);
      } finally {
        initializingRef.current = false;
      }
    };

    if (projectId && serviceName) {
      initialize();
    }
  }, [projectId, serviceName, addLog, loadFromIndexedDB, pullFromGit]); // Only run when project/service changes

  /**
   * Handle file selection from tree
   * RepositoryTree passes a file object with { path, name, etc }
   * 
   * OPTIMIZATION: Only loads the specific selected file from IndexedDB,
   * not the entire project context. This prevents the 12+ second freeze
   * when switching between files.
   */
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    // Extract filePath - RepositoryTree passes an object with path property
    const filePath = typeof file === 'string' ? file : file.path;
    const lineNumber = typeof file === 'object' ? file.lineNumber : null;
    
    // Debug logging
    // eslint-disable-next-line no-console
    devLog('🔍 CodeViewWithLogger handleFileSelect received:', {file, filePath, lineNumber});
    
    if (!filePath) return;
    if (compactRef.current) { setTreeOpen(false); }
    setMissingFile(null);

    setIsLoadingFile(true);
    setSelectedFile(filePath);
    setSelectedLineNumber(lineNumber);
    addLog(`📂 Opening file: ${filePath}${lineNumber ? ` (line ${lineNumber})` : ''}`, 'info');

    try {
      // A PDF is shown in the browser's own PDF viewer. It is fetched as bytes straight from vfs-service:
      // the IndexedDB copy of a vfs file is text, which would corrupt a PDF. Tried for every project, not only those
      // this browser marked as vfs-backed: a project opened from a link or made by an agent (MCP) is not marked, and
      // its images came out broken. Without vfs-service, or a 404, it falls through to the normal path.
      if (/\.pdf$/i.test(filePath)) {
        const pdfUrl = await loadBinaryBlobUrl(projectId, serviceName, filePath, 'application/pdf');
        if (pdfUrl) {
          const pdfFile = {
            path: filePath,
            name: filePath.split('/').pop(),
            content: '',
            lineNumber,
            isBinary: true,
            mimeType: 'application/pdf',
            binaryUrl: pdfUrl
          };
          setFileContent('');
          setSelectedFileObject(pdfFile);
          addLog(`📄 Opened PDF ${filePath}`, 'success');
          if (onFileSelect) { onFileSelect(filePath, pdfFile); }
          return;
        }
        // not found / unreadable: fall through to the normal path (shows the usual "not found" handling)
      }

      // An image is read the same way, for the same reason: as text it comes back with its bytes replaced, and the
      // viewer shows a broken picture. (SVG is text and stays on the normal path.)
      const imageExtension = (/\.(png|jpg|jpeg|gif|bmp|ico|webp|tiff|tif)$/i.exec(filePath) || [])[1];
      if (imageExtension) {
        const imageMime = IMAGE_MIME_BY_EXTENSION[imageExtension.toLowerCase()];
        const imageUrl = await loadBinaryBlobUrl(projectId, serviceName, filePath, imageMime);
        if (imageUrl) {
          const imageFile = {
            path: filePath,
            name: filePath.split('/').pop(),
            content: '',
            lineNumber,
            isBinary: true,
            mimeType: imageMime,
            binaryUrl: imageUrl
          };
          setFileContent('');
          setSelectedFileObject(imageFile);
          addLog(`🖼️ Opened image ${filePath}`, 'success');
          if (onFileSelect) { onFileSelect(filePath, imageFile); }
          return;
        }
        // not found / unreadable: fall through to the normal path
      }

      // Load ONLY this specific file from IndexedDB - don't reload entire project
      const fileData = await indexedDbFileStorage.getFile(projectId, serviceName, filePath);
      
      if (fileData) {
        // Detect if file is binary based on extension (fallback if isBinary flag not set)
        // SVG files are text-based XML, not binary - treat them as editable text
        const isImageFile = /\.(png|jpg|jpeg|gif|bmp|ico|webp|tiff|tif)$/i.test(filePath);
        const isSvg = /\.svg$/i.test(filePath);
        const isBinary = fileData.isBinary || isImageFile; // SVG excluded from binary
        
        // Determine MIME type - normalize extensions
        let mimeType = fileData.mimeType;
        if (!mimeType && (isImageFile || isSvg)) {
          const ext = filePath.split('.').pop().toLowerCase();
          const mimeTypeMap = {
            'png': 'image/png',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'gif': 'image/gif',
            'bmp': 'image/bmp',
            'ico': 'image/x-icon',
            'svg': 'image/svg+xml',
            'webp': 'image/webp',
            'tiff': 'image/tiff',
            'tif': 'image/tiff'
          };
          mimeType = mimeTypeMap[ext] || 'application/octet-stream';
        }
        
        // For binary images (not SVG), create blob URL for preview
        let binaryUrl = null;
        if (isBinary && mimeType?.startsWith('image/') && !isSvg) {
          // eslint-disable-next-line no-console
          devLog('🖼️ Creating blob URL for image:', filePath, {isBinary, mimeType});
          binaryUrl = await indexedDbFileStorage.getFileAsUrl(projectId, serviceName, filePath);
          // eslint-disable-next-line no-console
          devLog('🔗 Blob URL created:', binaryUrl ? 'SUCCESS' : 'FAILED', binaryUrl?.substring(0, 50));
        }
        
        // Create full file object with all metadata
        const fileWithMetadata = {
          ...fileData,
          path: filePath,
          name: filePath.split('/').pop(),
          lineNumber,
          isBinary,
          mimeType,
          binaryUrl
        };
        
        setFileContent(fileData.content);
        setSelectedFileObject(fileWithMetadata); // Store full file object
        addLog(`✅ Loaded ${filePath} (${fileData.state})`, 'success');
        
        // Call onFileSelect callback if provided
        if (onFileSelect) {
          // eslint-disable-next-line no-console
          devLog('📤 Sending file to editor:', {path: filePath, isBinary, mimeType, hasBinaryUrl: !!binaryUrl});
          onFileSelect(filePath, fileWithMetadata);
        }
      } else {
        addLog(`⚠️ File not found: ${filePath}`, 'warning');
        setFileContent('');
      }
    } catch (error) {
      addLog(`❌ Error loading file: ${error.message}`, 'error');
      setFileContent('');
    } finally {
      setIsLoadingFile(false);
    }
  }, [projectId, serviceName, addLog, onFileSelect]);

  /**
   * Handle file content changes from Monaco
   * 
   * OPTIMIZATION: Save file without triggering full project reload.
   * Only refresh the file tree metadata, not all file contents.
   */
  const handleFileChange = useCallback(async (filePath, newContent) => {
    if (!filePath) return;

    try {
      addLog(`💾 Saving changes to ${filePath}...`, 'info');
      
      // Save to IndexedDB
      await indexedDbFileStorage.saveFile(
        projectId,
        serviceName,
        filePath,
        newContent,
        { branch }
      );

      addLog(`✅ Saved ${filePath}`, 'success');
      
      if (onFileChange) {
        onFileChange(filePath, newContent);
      }

      // Refresh file tree to show modified state (but only tree metadata, not all content)
      // This is much faster than loading all file contents
      await loadFromIndexedDB();
    } catch (error) {
      addLog(`❌ Error saving file: ${error.message}`, 'error');
    }
  }, [projectId, serviceName, branch, addLog, onFileChange, loadFromIndexedDB]);

  /**
   * Handle manual refresh from git
   */
  const handleRefresh = useCallback(async () => {
    addLog('🔄 Refreshing from Git...', 'info');
    await pullFromGit();
  }, [addLog, pullFromGit]);

  /**
   * Handle push to git
   */
  const pushToGit = useCallback(async () => {
    addLog('🚀 Pushing to Git...', 'info');
    try {
      // Get modified files from IndexedDB
      const modifiedFiles = await indexedDbFileStorage.getModifiedFiles(projectId, serviceName);
      
      if (modifiedFiles.length === 0) {
        addLog('⚠️ No modified files to push', 'warning');
        return;
      }
      
      addLog(`📤 Pushing ${modifiedFiles.length} modified file(s)...`, 'info');
      
      // Dispatch event for push operation
      window.dispatchEvent(new CustomEvent('gitPushRequested', {
        detail: {
          projectId,
          serviceName,
          files: modifiedFiles
        }
      }));
      
      addLog('✅ Push completed successfully', 'success');
    } catch (error) {
      addLog(`❌ Push failed: ${error.message}`, 'error');
      throw error;
    }
  }, [projectId, serviceName, addLog]);

  /**
   * Listen for git pull completed events and update project context
   */
  useEffect(() => {
    const handleGitPullCompleted = async (event) => {
      const {projectId: pulledProjectId, serviceName: pulledServiceName} = event.detail || {};
      
      // Only handle if this is for our current service
      if (pulledProjectId !== projectId || pulledServiceName !== serviceName) {
        return;
      }
      
      addLog('✅ Pull completed successfully', 'success');
      
      try {
        // Refresh project context with newly pulled files from IndexedDB
        const filesArray = await indexedDbFileStorage.getAllFiles(projectId, serviceName);
        const allFiles = await Promise.all(filesArray.map(async (fileObj) => {
          return {
            path: fileObj.filePath,
            name: fileObj.fileName,
            content: fileObj.content || '',
            isNew: fileObj.state === 'new',
            modified: fileObj.state !== 'original'
          };
        }));
        
        addLog(`📂 Refreshed ${allFiles.length} files from IndexedDB`, 'info');
        
        // Clear auto-trigger flag if we have files
        if (allFiles.length > 0 && autoTriggeredPullRef?.current) {
          const serviceKey = `${projectId}|${serviceName}`;
          autoTriggeredPullRef.current.delete(serviceKey);
        }
        
        // Update project context via callback
        if (onProjectContextChange && selectedProject && selectedResource) {
          // Use internal selectedFile state for context
          const currentFileData = selectedFile ? {
            name: selectedFile.split('/').pop(),
            path: selectedFile,
            content: (await indexedDbFileStorage.getFile(
              projectId,
              serviceName,
              selectedFile
            ))?.content || '',
            language: selectedFile.split('.').pop()
          } : null;
          
          onProjectContextChange({
            tenant: selectedTenant,
            project: selectedProject,
            revision: selectedRevision,
            selectedResource: selectedResource,
            selectedMicroservice: {
              name: serviceName,
              type: selectedResource.data?.type,
              repositoryUrl: repositoryUrl
            },
            selectedFile: currentFileData,
            allFiles: allFiles,
            configuration: configuration,
            collections: collections,
            projectId: projectId
          });
        }
        
        // Refresh preview if applicable
        if (previewUrl && selectedResource && selectedResource.data?.name === pulledServiceName) {
          if (setPreviewUrl) setPreviewUrl(null);
          if (setPreviewError) setPreviewError(null);
          if (setForceGitFetch) setForceGitFetch(true);
          
          addLog('🔄 Refreshing preview...', 'info');
          
          // Small delay to ensure IndexedDB is fully updated
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        // Reload files to update tree
        await loadFromIndexedDB();
      } catch (error) {
        addLog(`❌ Failed to refresh after pull: ${error.message}`, 'error');
        console.error('Error in gitPullCompleted handler:', error);
      }
    };

    window.addEventListener('gitPullCompleted', handleGitPullCompleted);
    
    return () => {
      window.removeEventListener('gitPullCompleted', handleGitPullCompleted);
    };
  }, [projectId, serviceName, selectedProject, selectedRevision, selectedTenant, selectedResource, repositoryUrl, configuration, collections, previewUrl, onProjectContextChange, setPreviewUrl, setPreviewError, setForceGitFetch, autoTriggeredPullRef, addLog, loadFromIndexedDB]);

  // Listen for Git repository clone completion
  useEffect(() => {
    const handleGitCloneCompleted = async (event) => {
      const { owner, repo, clone_path, files_count } = event.detail;
      
      addLog(`✅ Git repository ${owner}/${repo} cloned successfully`, 'success');
      addLog(`📂 Location: ${clone_path}`, 'info');
      addLog(`📊 Files: ${files_count} (indexing in progress...)`, 'info');
    };
    
    const handleGitCloneIndexed = async (event) => {
      const { repo, indexed_count } = event.detail;
      
      addLog(`✅ Repository ${repo} indexed: ${indexed_count} files`, 'success');
      addLog(`📂 Files available under: source-code-for-analysis/${repo}/`, 'info');
      
      // Refresh file tree to show cloned files
      try {
        await loadFromIndexedDB();
        addLog('🔄 File tree refreshed', 'success');
      } catch (error) {
        addLog(`⚠️ Could not refresh file tree: ${error.message}`, 'warning');
      }
    };

    window.addEventListener('git_clone_completed', handleGitCloneCompleted);
    window.addEventListener('git_clone_indexed', handleGitCloneIndexed);
    
    return () => {
      window.removeEventListener('git_clone_completed', handleGitCloneCompleted);
      window.removeEventListener('git_clone_indexed', handleGitCloneIndexed);
    };
  }, [addLog, loadFromIndexedDB]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    pullFromGit: async () => {
      addLog('🔄 Pull triggered from toolbar', 'info');
      await pullFromGit();
    },
    pushToGit: async () => {
      await pushToGit();
    },
    refresh: async () => {
      await handleRefresh();
    },
    triggerDeployment: async (environment, revisionId) => {
      if (!selectedProject?.id || !environment) {
        addLog('❌ Missing project or environment for deployment', 'error');
        throw new Error('Missing required deployment parameters');
      }
      
      addLog(`🚀 Deploying to ${environment.envId}...`, 'info');
      
      try {
        const result = await miaPlatformDataService.triggerDeployment(selectedProject.id, {
          deployType: 'deploy_all',
          environment: environment.envId,
          refType: 'revision',
          revision: revisionId
        });
        
        const pipelineId = result.pipelineId || result.id;
        
        if (!pipelineId) {
          throw new Error('Invalid deployment response: missing pipeline ID');
        }
        
        addLog(`✅ Deployment triggered (Pipeline: ${pipelineId})`, 'success');
        
        return { pipelineId, result };
      } catch (error) {
        addLog(`❌ Deployment failed: ${error.message}`, 'error');
        throw error;
      }
    },
    getPipelineStatus: async (pipelineId, environmentId) => {
      if (!selectedProject?.id || !pipelineId) {
        throw new Error('Missing project or pipeline ID');
      }
      
      try {
        const status = await miaPlatformDataService.getPipelineStatus(
          selectedProject.id,
          pipelineId,
          environmentId
        );
        
        return status;
      } catch (error) {
        addLog(`❌ Failed to fetch pipeline status: ${error.message}`, 'error');
        throw error;
      }
    }
  }), [pullFromGit, pushToGit, handleRefresh, addLog, selectedProject, miaPlatformDataService]);

  // Handle errors from Monaco editor
  const handleEditorErrors = useCallback((filePath, errors) => {
    setFileErrors(prev => {
      if (errors.length === 0) {
        // Only clear if this is the currently open file
        if (filePath === currentOpenFileRef.current) {
          const {[filePath]: removed, ...rest} = prev;
          return rest;
        }
        return prev;
      }
      // Update errors for this file
      return {...prev, [filePath]: errors};
    });
  }, []);

  // Handle "Ask AI to Fix" from tree view
  const handleAskAIToFix = useCallback((filePath, fileContent, errorDetails) => {
    // Dispatch event to communicate with App component
    window.dispatchEvent(new CustomEvent('askAIFromFile', {
      detail: {
        filePath,
        fileContent,
        errorDetails
      }
    }));
  }, []);

  // Load initial file if specified
  useEffect(() => {
    if (initialFilePath && hasLoadedFromIndexedDB) {
      handleFileSelect(initialFilePath);
    }
  }, [initialFilePath, hasLoadedFromIndexedDB, handleFileSelect]);

  // No project or service yet: on a reload the page knows them a moment later (the URL is read before the project
  // loads), so this is the loading state, never an error.
  if (!projectId || !serviceName) {
    return (
      <div className="code-view-with-logger" data-testid="code-view-waiting">
        <LoadingFile />
      </div>
    );
  }

  return (
    <div className="code-view-with-logger">
      {/* Main content area */}
      <div className={`code-view-main${compact ? ' is-compact' : ''}${compact && treeOpen ? ' is-tree-open' : ''}`} ref={mainRef}>
        {compact && (
          <div className="code-view-compact-bar">
            <button
              aria-controls="code-view-tree"
              aria-expanded={treeOpen}
              className="code-view-tree-toggle"
              data-testid="code-view-tree-toggle"
              onClick={() => setTreeOpen((open) => !open)}
              type="button"
            >
              <Icon name="folder" size={16} />
              <span>Files</span>
            </button>
            <span className="code-view-compact-path" title={selectedFile || ''}>{selectedFile ? selectedFile.split('/').pop() : 'No file open'}</span>
          </div>
        )}
        {compact && treeOpen && selectedFile && <div aria-hidden className="code-view-tree-backdrop" onClick={() => setTreeOpen(false)} />}
        {/* Left: File tree */}
        <div
          aria-label={compact ? 'Files' : undefined}
          className="code-view-sidebar"
          id="code-view-tree"
          ref={sidebarRef}
          role={compact ? 'region' : undefined}
          style={compact ? undefined : { width: sidebarWidth }}
        >
          <div className="sidebar-content">
            {isLoadingFiles ? (
              <div className="loading-state">
                <Icon name="loading" size={24} />
                <p>Loading files...</p>
              </div>
            ) : (
              <>
                {/* Always render RepositoryTree so it can listen for git events */}
                <RepositoryTree
                  repositoryUrl={repositoryUrl}
                  onFileSelect={handleFileSelect}
                  selectedFilePath={selectedFile || ''}
                  serviceName={serviceName}
                  projectId={projectId}
                  fileErrors={fileErrors}
                  onAskAIToFix={handleAskAIToFix}
                  isPullingFromGit={isPullingFromGit}
                  tenantId={selectedTenant?.tenantId || selectedTenant?.id}
                  branchName={branch}
                  vfsBacked={Boolean(selectedResource?.data?.vfsBacked)}
                  syncUrlOnFileSelect={syncUrlOnFileSelect}
                  forceFreshTree={forceFreshTree}
                  autoOpenPath={pickedPath || autoOpenPath}
                  autoOpenLine={pickedPath ? null : autoOpenLine}
                  autoOpenStrict={autoOpenStrict}
                  onAutoOpenMissing={handleAutoOpenMissing}
                  readOnly={readOnly}
                />
                {/* Show empty state overlay when no files. Not over a vfs-backed tree: it lists its folders from
                    vfs-service itself, and the local cache only holds the files opened (RepositoryTree's lazy tree). */}
                {fileTree.length === 0 && !(selectedResource?.data?.vfsBacked && !repositoryUrl) && (
                  <div className="empty-state">
                    <Icon name="folder" size={48} />
                    <p>No files available</p>
                    {error && <p className="error-text">{error}</p>}
                    {isPullingFromGit ? (
                      <div className="pulling-state">
                        <Icon name="loading" size={20} />
                        <span>Pulling...</span>
                      </div>
                    ) : (
                      <button
                        className="btn-pull"
                        onClick={handleRefresh}
                        type="button"
                      >
                        Pull from Git
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div
          className={`code-view-resizer${isResizingSidebar ? ' active' : ''}`}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize file tree"
          aria-valuemin={SIDEBAR_MIN}
          aria-valuemax={SIDEBAR_MAX}
          aria-valuenow={Math.round(sidebarWidth)}
          tabIndex={0}
          onPointerDown={handleSidebarResizeStart}
          onPointerMove={handleSidebarResizeMove}
          onPointerUp={handleSidebarResizeEnd}
          onPointerCancel={handleSidebarResizeEnd}
          onKeyDown={handleSidebarResizeKey}
          onDoubleClick={() => { setSidebarWidth(SIDEBAR_DEFAULT); persistSidebarWidth(SIDEBAR_DEFAULT); }}
          title="Drag to resize · double-click to reset"
        />

        {/* Right: Monaco editor */}
        <div className="code-view-editor">
          {isPullingFromGit && fileTree.length === 0 ? (
            <div className="loading-state">
              <HexagonalLoader message="Downloading code from Git..." />
            </div>
          ) : selectedFile && !missingFile ? (
            isLoadingFile ? (
              <LoadingFile />
            ) : (
              <>
                {repoLocked && (
                  <div className="repo-flag-banner" data-testid="code-view-repo-read-only" role="status">
                    Read-only: {lockedRepo.repo} is flagged read-only in this project.
                  </div>
                )}
                <MonacoFileEditor
                  file={memoizedFileObject}
                  onFileChange={(content) => handleFileChange(selectedFile, content)}
                  onErrorsChange={handleEditorErrors}
                  onSave={onSave}
                  isPreviewMode={isPreviewMode}
                  projectId={projectId}
                  serviceName={serviceName}
                  readOnly={readOnly || repoLocked}
                />
              </>
            )
          ) : missingFile ? (
            <div className="empty-state code-view-missing-file" data-testid="code-view-missing-file" role="status">
              <Icon name="warning" size={48} />
              <p><span className="code-view-missing-path">{missingFile.path}</span>{` isn't in ${serviceLabel(serviceName)} anymore`}</p>
              {missingFile.matches?.length > 0 ? (
                <>
                  <p style={{ opacity: 0.7, fontSize: '13px' }}>Closest matches:</p>
                  <ul className="code-view-missing-matches">
                    {missingFile.matches.map((match) => (
                      <li key={match}>
                        <button className="code-view-missing-match" onClick={() => setPickedPath(match)} type="button">{match}</button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p style={{ opacity: 0.7, fontSize: '13px' }}>No file with a similar name. Pick one in the tree.</p>
              )}
            </div>
          ) : isEmptyProject ? (
            <div className="empty-state">
              <Icon name="folder" size={48} />
              <p>Empty project</p>
              <p style={{ opacity: 0.7, fontSize: '13px' }}>
                Start a conversation in the chat to build it.
              </p>
              {onSwitchToChat && (
                <button
                  style={{ padding: '8px 20px', background: '#007acc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', marginTop: '8px' }}
                  onClick={onSwitchToChat}
                  type="button"
                >
                  {'Go to Chat'}
                </button>
              )}
            </div>
          ) : waitingForLinkedFile ? (
            <LoadingFile />
          ) : (
            <div className="empty-state">
              <Icon name="file" size={48} />
              <p>Select a file to edit</p>
            </div>
          )}
        </div>
      </div>
      {terminal && !readOnly && <CodeTerminalDock enabled projectId={projectId} />}
    </div>
  );
});

CodeViewWithLogger.displayName = 'CodeViewWithLogger';

CodeViewWithLogger.propTypes = {
  projectId: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired,
  repositoryUrl: PropTypes.string,
  branch: PropTypes.string,
  onFileSelect: PropTypes.func,
  onFileChange: PropTypes.func,
  onSave: PropTypes.func,
  initialFilePath: PropTypes.string,
  autoOpenPath: PropTypes.string,
  autoOpenLine: PropTypes.number,
  autoOpenStrict: PropTypes.bool,
  readOnly: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  // Project context
  selectedProject: PropTypes.object,
  selectedRevision: PropTypes.object,
  selectedTenant: PropTypes.object,
  selectedResource: PropTypes.object,
  configuration: PropTypes.object,
  collections: PropTypes.object,
  onProjectContextChange: PropTypes.func,
  // Preview control
  previewUrl: PropTypes.string,
  setPreviewUrl: PropTypes.func,
  setPreviewError: PropTypes.func,
  setForceGitFetch: PropTypes.func,
  autoTriggeredPullRef: PropTypes.object,
  terminal: PropTypes.bool
};

export default CodeViewWithLogger;
